<script setup>
  import {computed, ref} from "vue";

  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {router} from "@inertiajs/vue3";

  defineOptions({
    name: "PatientsPatientDocumentRow",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    attributeName: {
      type: String,
      required: true,
    },
  });

  const refConfirmationDialog = ref(null);

  const confirmation = computed(() => {
    return (props.patient?.confirmations ?? []).find((confirmation) => {
      return confirmation.subject === props.attributeName;
    });
  });

  function save(value) {
    router.post(
      route("confirmations.store", {patient: props.patient.id}),
      {
        type: value ? "accepted" : "rejected",
        subject: props.attributeName,
      },
      {
        preserveScroll: true,
      },
    );
  }
</script>

<template>
  <div class="flex items-start">
    <ComponentConfirmationDialog
      ref="refConfirmationDialog"
      title="Widerruf"
      :test-id="getTestId(`document-row-${attributeName}-reject-confirm`)"
      @confirmed="save(false)"
    >
      <template #content>
        Die am {{ confirmation?.created_at ?? patient[attributeName] }}
        <template v-if="confirmation?.signer === 'user'">handschriftlich in der Apotheke</template>
        <template v-else-if="confirmation?.signer === 'patient'">digital durch den Patienten</template>
        <template v-else-if="confirmation?.signer === 'representative'">
          digital durch die gesetzliche Vertretung
        </template>
        <template v-else>handschriftlich in Apotheke</template>
        erteilte Einwilligung für
        <span class="font-semibold">
          <slot />
        </span>
        wird widerrufen.
      </template>
    </ComponentConfirmationDialog>

    <button
      v-if="patient[attributeName]"
      class="group mr-2 rounded-full"
      :data-test="getTestId(`document-row-${attributeName}-toggle-circle`)"
      @click="$refs.refConfirmationDialog.open()"
    >
      <ComponentIcon class="block text-2xl text-mcgreen group-hover:hidden">check_circle</ComponentIcon>
      <ComponentIcon
        class="hidden text-2xl text-gray-500 group-hover:block group-hover:rounded-full group-hover:outline-gray-400 group-hover:outline-dashed"
      >
        cancel
      </ComponentIcon>
    </button>

    <button
      v-else
      class="group mr-2 rounded-full"
      :data-test="getTestId(`document-row-${attributeName}-toggle-circle`)"
      @click="save(true)"
    >
      <template v-if="confirmation">
        <ComponentIcon class="text-2xl text-mcred group-hover:hidden">cancel</ComponentIcon>
        <ComponentIcon
          class="hidden text-2xl text-gray-500 group-hover:block group-hover:rounded-full group-hover:outline-gray-400 group-hover:outline-dashed"
        >
          check_circle
        </ComponentIcon>
      </template>
      <template v-else>
        <ComponentIcon class="text-2xl text-gray-500 group-hover:hidden">radio_button_unchecked</ComponentIcon>
        <ComponentIcon class="hidden text-2xl text-gray-500 group-hover:block">check_circle_outline</ComponentIcon>
      </template>
    </button>

    <div>
      <div>
        <slot />
      </div>

      <div class="text-xs">
        <span v-if="confirmation?.signer === 'user'">
          <template v-if="confirmation?.type === 'accepted'">handschriftlich in Apotheke</template>
          <template v-else-if="confirmation?.type === 'rejected'">
            durch Apotheke widerrufen am {{ confirmation.created_at }}
          </template>
        </span>
        <span v-else-if="confirmation?.signer === 'patient'">
          <template v-if="confirmation?.type === 'accepted'">
            digital durch Patienten am {{ confirmation.created_at }}
          </template>
          <template v-else-if="confirmation?.type === 'rejected'">
            durch Patienten widerrufen am {{ confirmation.created_at }}
          </template>
        </span>
        <span v-else-if="confirmation?.signer === 'representative'">
          <template v-if="confirmation?.type === 'accepted'">
            digital durch gesetzliche Vertretung am {{ confirmation.created_at }}
          </template>
          <template v-else-if="confirmation?.type === 'rejected'">
            durch gesetzliche Vertretung widerrufen am {{ confirmation.created_at }}
          </template>
        </span>
        <span v-else-if="patient[attributeName]">handschriftlich in Apotheke</span>
        <span v-else>
          <button class="cursor-pointer text-mcred hover:underline" @click.prevent="save(false)">
            als widerrufen kennzeichnen
          </button>
        </span>
      </div>
    </div>
  </div>
</template>
